import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";

/**
 * Big animated rating reveal shown on the 2nd screen after a song ends.
 * Displays the average vote (0-10) and the number of votes received.
 */
const RatingOverlay = ({ average, count, min, max }) => {
  const [displayed, setDisplayed] = useState(0);

  useEffect(() => {
    let frame;
    const start = performance.now();
    const duration = 900;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayed(Number((average * eased).toFixed(1)));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [average]);

  const tone =
    average >= 9 ? "text-secondary" : average >= 7 ? "text-primary" : "text-white";
  const glow = average >= 9 ? "neon-cyan" : "neon-pink";

  return (
    <div
      className="fixed inset-0 z-[55] flex flex-col items-center justify-center bg-black/90 backdrop-blur-xl overflow-hidden"
      data-testid="rating-overlay"
    >
      <div className="absolute inset-0 grain pointer-events-none" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, rgba(134,46,250,0.2), transparent 50%), radial-gradient(circle at 50% 100%, rgba(0,219,255,0.14), transparent 60%)",
        }}
      />

      <div className="relative z-10 text-center px-6 animate-presentation-in">
        <div className="inline-flex items-center gap-3 mb-6">
          <Star className="w-6 h-6 text-secondary" fill="currentColor" />
          <p className="font-mono text-xs sm:text-sm tracking-[0.4em] uppercase text-secondary">
            Promedio de la audiencia
          </p>
          <Star className="w-6 h-6 text-secondary" fill="currentColor" />
        </div>

        <div className={`font-display font-black leading-none ${tone} ${glow} animate-glow-pulse`}>
          <span className="text-[10rem] sm:text-[14rem] md:text-[18rem]">
            {displayed.toFixed(1)}
          </span>
          <span className="font-display text-4xl sm:text-6xl text-white/40 ml-2">/10</span>
        </div>

        <div className="mt-8 flex items-center justify-center gap-6 text-muted-foreground font-mono uppercase tracking-widest text-xs sm:text-sm">
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-primary" />
            {count} voto{count === 1 ? "" : "s"}
          </span>
          {typeof min === "number" && (
            <span className="opacity-70">
              min {min} · max {max}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default RatingOverlay;
